import React from "react";
import { Link } from "react-router-dom";
import { SmsReplyItem } from "@/components/SmsReplyItem";
import {
  AlertTriangle, CalendarClock, ShieldAlert, Truck, PackageSearch, TrendingUp,
  HeartPulse, Star, Plane, ListChecks, Trophy, Users, Inbox, GraduationCap,
  CalendarDays, PackageX, ScanLine, Snowflake, ExternalLink, ChevronRight, ClipboardList, MessageSquare,
} from "lucide-react";

export const EXTRA_KEYS = [
  "attention", "today_schedule", "jobs_at_risk", "engineer_status", "stock_alerts",
  "sales_snapshot", "company_pulse", "trustpilot", "my_leave", "my_actions", "my_progress",
  "cover_gaps", "approvals", "training_quals", "attendance_overview",
  "low_stock_reorder", "count_discrepancies", "slow_moving", "product_requests", "unread_sms",
];

export const WIDGET_GROUPS = [
  {
    cat: "Operations",
    items: [
      { key: "attention", label: "Needs attention today", hint: "Overdue, unassigned, shortages & issues", Icon: AlertTriangle },
      { key: "today_schedule", label: "Today's schedule", hint: "Installs, deliveries, service & collections", Icon: CalendarClock },
      { key: "jobs_at_risk", label: "Jobs at risk", hint: "Missing stock, forms, payment or engineer", Icon: ShieldAlert },
      { key: "engineer_status", label: "Engineer status", hint: "Travelling, on site, late & available", Icon: Truck },
      { key: "stock_alerts", label: "Stock alerts", hint: "Low, reserved & needed soon", Icon: PackageSearch },
      { key: "sales_snapshot", label: "Sales snapshot", hint: "Enquiries, quotes & orders this week", Icon: TrendingUp },
      { key: "company_pulse", label: "Company pulse", hint: "Jobs done, feedback & achievements", Icon: HeartPulse },
      { key: "trustpilot", label: "Latest Trustpilot review", hint: "Most recent customer review", Icon: Star },
    ],
  },
  {
    cat: "My dashboard",
    items: [
      { key: "my_leave", label: "My leave", hint: "Allowance, approved dates & requests", Icon: Plane },
      { key: "my_actions", label: "My actions", hint: "Forms, announcements & timesheets", Icon: ListChecks },
      { key: "my_progress", label: "My progress", hint: "Completed jobs, compliments & goals", Icon: Trophy },
    ],
  },
  {
    cat: "Manager / HR",
    items: [
      { key: "cover_gaps", label: "Cover gaps", hint: "Days leave thins available cover", Icon: Users },
      { key: "approvals", label: "Requests awaiting approval", hint: "Leave, swaps, timesheets & expenses", Icon: Inbox },
      { key: "training_quals", label: "Training & qualifications", hint: "Expiring certs & overdue training", Icon: GraduationCap },
      { key: "attendance_overview", label: "Attendance overview", hint: "Absence & overtime trends", Icon: CalendarDays },
    ],
  },
  {
    cat: "Communications",
    items: [
      { key: "unread_sms", label: "Unread customer SMS", hint: "Jobs with a customer reply awaiting a response", Icon: MessageSquare },
    ],
  },
  {
    cat: "Stock & warehouse",
    items: [
      { key: "low_stock_reorder", label: "Low stock / reorder", hint: "Below minimum, demand & incoming", Icon: PackageSearch },
      { key: "count_discrepancies", label: "Count discrepancies", hint: "Scanned vs recorded quantities", Icon: ScanLine },
      { key: "slow_moving", label: "Slow-moving stock", hint: "Idle, display & clearance lines", Icon: Snowflake },
      { key: "product_requests", label: "Outstanding product requests", hint: "Parts short across all jobs", Icon: ClipboardList },
    ],
  },
];

const ICONS = Object.fromEntries(WIDGET_GROUPS.flatMap((g) => g.items.map((i) => [i.key, i.Icon])));

const TONE = {
  danger: "text-rose-600",
  warn: "text-amber-600",
  good: "text-emerald-600",
  default: "text-slate-900",
};

function StatRow({ r }) {
  const val = (
    <span className={`text-sm font-bold tabular-nums ${TONE[r.tone] || TONE.default}`} data-testid="widget-stat-value">
      {typeof r.value === "number" ? r.value.toLocaleString() : r.value}
    </span>
  );
  const label = (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <span className="text-sm text-slate-600 truncate">{r.label}</span>
        {r.derived && (
          <span className="text-[9px] uppercase tracking-wide font-semibold text-slate-400 bg-slate-100 rounded px-1 py-0.5" title="Estimated / derived value">est</span>
        )}
      </div>
      {r.hint && <div className="text-[11px] text-slate-400 truncate">{r.hint}</div>}
    </div>
  );
  const body = (
    <div className="flex items-center justify-between gap-3 py-1.5">
      {label}
      <div className="flex items-center gap-1 shrink-0">{val}{r.link && <ChevronRight className="w-3.5 h-3.5 text-slate-300" />}</div>
    </div>
  );
  return r.link ? (
    <Link to={r.link} className="block -mx-2 px-2 rounded-lg hover:bg-slate-50 transition-colors">{body}</Link>
  ) : body;
}

function CardShell({ id, title, Icon, footnote, action, onAction, children }) {
  const renderAction = () => {
    if (!action) return null;
    if (action.modal) {
      return (
        <button type="button" onClick={() => onAction && onAction(action.modal)} data-testid={`widget-${id}-action`}
          className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 px-3 py-2 text-xs font-semibold text-white">
          {action.label}
        </button>
      );
    }
    if (action.external) {
      return (
        <a href={action.to} target="_blank" rel="noreferrer" data-testid={`widget-${id}-action`}
          className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-indigo-600">
          {action.label}<ExternalLink className="w-3.5 h-3.5" />
        </a>
      );
    }
    return (
      <Link to={action.to} data-testid={`widget-${id}-action`}
        className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 px-3 py-2 text-xs font-semibold text-white">
        {action.label}
      </Link>
    );
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 flex flex-col" data-testid={`widget-${id}`}>
      <h2 className="text-base font-bold text-slate-900 flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-indigo-500 shrink-0" />}<span className="truncate">{title}</span>
      </h2>
      <div className="flex-1">{children}</div>
      {footnote && <p className="text-[11px] text-slate-400 mt-3 pt-2 border-t border-slate-100">{footnote}</p>}
      {renderAction()}
    </div>
  );
}

function Stars({ n }) {
  return (
    <span className="inline-flex" aria-label={`${n} stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`w-4 h-4 ${i < n ? "text-emerald-500 fill-emerald-500" : "text-slate-200"}`} />
      ))}
    </span>
  );
}

export function WidgetCard({ w, onAction }) {
  const Icon = ICONS[w.id];
  if (w.kind === "trustpilot") {
    const rv = w.review || {};
    return (
      <CardShell id={w.id} title={w.title} Icon={Icon}
        action={{ label: "Read on Trustpilot", to: w.url, external: true }}>
        <div className="flex items-center justify-between mb-3">
          <Stars n={rv.stars || 5} />
          <span className="text-xs text-slate-500"><b className="text-slate-800">{w.score}</b>/5 · {(w.total || 0).toLocaleString()} reviews</span>
        </div>
        {rv.title && <div className="text-sm font-bold text-slate-900">{rv.title}</div>}
        <p className="text-sm text-slate-600 mt-1 leading-relaxed line-clamp-4">“{rv.text}”</p>
        <div className="text-[11px] text-slate-400 mt-2">— {rv.author}{rv.date ? ` · ${rv.date}` : ""}</div>
      </CardShell>
    );
  }
  if (w.kind === "schedule") {
    return (
      <CardShell id={w.id} title={w.title} Icon={Icon} footnote={w.footnote} action={w.action} onAction={onAction}>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {w.rows.map((r) => (
            <div key={r.label} className="rounded-xl border border-slate-200 p-2.5">
              <div className="text-xl font-bold text-slate-900 tabular-nums">{r.value}</div>
              <div className="text-[11px] text-slate-500 truncate">{r.label}</div>
            </div>
          ))}
        </div>
        <div className="divide-y divide-slate-100">
          {(w.items || []).length === 0 ? (
            <div className="text-xs text-slate-400 py-3 text-center">No shifts scheduled today.</div>
          ) : w.items.map((it, i) => (
            <div key={i} className="flex items-center justify-between gap-2 py-1.5">
              <div className="min-w-0">
                <div className="text-sm text-slate-700 truncate">{it.title}</div>
                <div className="text-[11px] text-slate-400 truncate">{it.meta}</div>
              </div>
              <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 shrink-0">{it.status}</span>
            </div>
          ))}
        </div>
      </CardShell>
    );
  }
  if (w.kind === "inbox") {
    return (
      <CardShell id={w.id} title={w.title} Icon={Icon} footnote={w.footnote} action={w.action} onAction={onAction}>
        {(w.items || []).length === 0 ? (
          <div className="text-xs text-slate-400 py-6 text-center" data-testid="widget-unread-sms-empty">No unread customer replies right now.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {w.items.map((it, i) => <SmsReplyItem key={it.conversation_id || i} it={it} i={i} />)}
          </div>
        )}
        {(w.count || 0) > (w.items || []).length && (
          <div className="text-[11px] text-slate-400 mt-2">+{w.count - w.items.length} more awaiting reply</div>
        )}
      </CardShell>
    );
  }
  // stats
  return (
    <CardShell id={w.id} title={w.title} Icon={Icon} footnote={w.footnote} action={w.action} onAction={onAction}>
      <div className="divide-y divide-slate-100">
        {w.rows.map((r) => <StatRow key={r.label} r={r} />)}
      </div>
    </CardShell>
  );
}
